/* eslint-disable @typescript-eslint/no-explicit-any */
import { i18n } from '@/plugins/i18n';
import { useSpreadsheetErrorsStore } from '@/stores/errors';
import { useSessionStore } from '@/stores/session';
import { useToasterStore } from '@/stores/toaster';
import { useUserStore } from '@/stores/user';
import axios, { AxiosError } from 'axios';
import type { AxiosRequestConfig } from 'axios';

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  skipErrorHandling?: boolean;
}

const baseConfig = {
  baseURL: `${import.meta.env.VITE_API_URL}/api`,
  timeout: 5 * 60 * 1000,
  withCredentials: true,
};

const axiosInstance = axios.create(baseConfig);

axiosInstance.interceptors.request.use(async (config) => {
  const sessionStore = useSessionStore();
  
  if (config.headers && sessionStore.hasSession && !sessionStore.isHttpOnlySession) {
    config.headers['HRMANAGER_EMAIL'] = sessionStore.email;
    config.headers['HRMANAGER_TOKEN'] = sessionStore.token;
  }
  
  if (config.headers && sessionStore.hasTemporarySession) {
    config.headers['HRMANAGER_EMAIL'] = sessionStore.email;
    config.headers['HRMANAGER_TEMPORARY_AUTH_TOKEN'] = sessionStore.temporaryToken;
  }
  
  if (config.headers) {
    config.headers['Cache-Control'] = 'no-cache';
  }
  
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    if (!(error.config as ExtendedAxiosRequestConfig)?.skipErrorHandling) {
      handleError(error);
    }
    throw error;
  },
);

let isLoggingOut = false;

const handleError = (request: AxiosError<any, any>) => {
  const { t } = i18n.global;
  const toasterStore = useToasterStore();
  const userStore = useUserStore();
  const sessionStore = useSessionStore();
  
  try {
    const status = request.response?.status;
    const errors = request.response?.data?.errors;
    switch (status) {
      case 401:
        if (isLoggingOut) break;
        
        if (!sessionStore.isHttpOnlySession) {
          isLoggingOut = true;
          toasterStore.addMessage({ text: t('errors.session_expired') });
          userStore.logout().finally(() => { isLoggingOut = false; });
        } else {
          if (userStore.currentUser) {
            isLoggingOut = true;
            toasterStore.addMessage({ text: t('errors.session_expired') });
            userStore.logout().finally(() => { isLoggingOut = false; });
          }
        }
        break;
      case 403:
        toasterStore.addMessage({ text: t('errors.insufficient_permission') });
        break;
      case 404:
        toasterStore.addMessage({ text: t('errors.not_found_error') });
        break;
      case 422:
        if (errors) {
          if (errors instanceof Array && errors[0] instanceof Object) {
            useSpreadsheetErrorsStore().setErrors(errors);
          } else {
            if ((request.config as ExtendedAxiosRequestConfig)?.skipErrorHandling) {
              return errors.join(', ');
            }

            toasterStore.addMessage({ text: errors.join(', ') });
          }
        } else {
          toasterStore.addMessage({ text: t('errors.default_error') });
        }
        break;
      default:
        toasterStore.addMessage({ text: t('errors.default_error') });
        break;
    }
  } catch (e) {
    toasterStore.addMessage({ text: t('errors.default_error') });
  }
};

export const apiService = {
  get: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const serializedParams: Record<string, any> = {};
    if (params) {
      Object.keys(params).forEach(key => {
        if (key === 'scopes' && typeof params[key] === 'object' && params[key] !== null) {
          serializedParams[key] = JSON.stringify(params[key]);
        } else {
          serializedParams[key] = params[key];
        }
      });
    }
    const { data } = await axiosInstance.get<ResponseType>(path, { params: serializedParams });

    return data;
  },
  getBlob: async (path: string, params?: Record<string, any>) => {
    const serializedParams: Record<string, any> = {};
    if (params) {
      Object.keys(params).forEach(key => {
        if (key === 'scopes' && typeof params[key] === 'string') {
          serializedParams[key] = params[key];
        } else if (key === 'scopes' && typeof params[key] === 'object' && params[key] !== null) {
          serializedParams[key] = JSON.stringify(params[key]);
        } else {
          serializedParams[key] = params[key];
        }
      });
    }
    const response = await axiosInstance.get(path, { params: serializedParams, responseType: 'blob' });

    if (response.status >= 200 && response.status < 300) {
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const blob = response.data as Blob;
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          const error = new Error(errorData?.error || errorData?.message || 'Erro ao processar resposta');
          (error as any).response = { data: errorData };
          throw error;
        } catch (parseError: any) {
          if (parseError instanceof Error && parseError.message !== 'Erro ao processar resposta') {
            throw parseError;
          }
          throw new Error('Resposta inválida do servidor');
        }
      }
    }

    return response.data;
  },
  postBlob: async (path: string, body?: Record<string, any>) => {
    const response = await axiosInstance.post(path, body, { responseType: 'blob' });

    if (response.status >= 200 && response.status < 300) {
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const blob = response.data as Blob;
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          const error = new Error(errorData?.error || errorData?.message || 'Erro ao processar resposta');
          (error as any).response = { data: errorData };
          throw error;
        } catch (parseError: any) {
          if (parseError instanceof Error && parseError.message !== 'Erro ao processar resposta') {
            throw parseError;
          }
          throw new Error('Resposta inválida do servidor');
        }
      }
    }

    return response.data;
  },
  post: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const { data } = await axiosInstance.post<ResponseType>(path, params);

    return data;
  },
  postWithoutErrorHandling: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const { data } = await axiosInstance.post<ResponseType>(path, params, {
      skipErrorHandling: true,
    } as ExtendedAxiosRequestConfig);
    return data;
  },
  getWithoutErrorHandling: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const { data } = await axiosInstance.get<ResponseType>(path, {
      params,
      skipErrorHandling: true,
    } as ExtendedAxiosRequestConfig);
    return data;
  },
  patch: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const { data } = await axiosInstance.patch<ResponseType>(path, params);

    return data;
  },
  put: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const { data } = await axiosInstance.put<ResponseType>(path, params);

    return data;
  },
  delete: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const { data } = await axiosInstance.delete<ResponseType>(path, { params });

    return data;
  },
  deleteWithoutErrorHandling: async <ResponseType>(path: string, params?: Record<string, any>) => {
    const { data } = await axiosInstance.delete<ResponseType>(path, {
      params,
      skipErrorHandling: true,
    } as ExtendedAxiosRequestConfig);

    return data;
  },
};
